<ng-container *transloco="let t">
  @if (isError()) {
    <ng-container *ngTemplateOutlet="errorTemplate"></ng-container>
  } @else if (isLoading()) {
    <ng-container *ngTemplateOutlet="loadingTemplate"></ng-container>
  } @else {
    <sbnb-nested-full-screen-modal (backIconClicked)="backIconClicked()">
      <ng-container ngProjectAs="content">
        <header class="mb-6 w-full">
          <h2 class="mb-2">{{ mode() === 'edit' ? 'Edit' : modeLabelMapper[mode()] }}</h2>
          <span class="text-night leading-6">Customize pricing and availability for selected dates.</span>
        </header>

        <form class="mb-24" [formGroup]="rulesetForm" (ngSubmit)="save()">
          @switch (activeFormArea()) {
            @case (null) {
              <ng-container *ngTemplateOutlet="formSummaryTemplate"></ng-container>
            }
            @case ('seasonal_adjustment') {
              <ng-container *ngTemplateOutlet="priceAdjustmentTemplate"></ng-container>
            }
            @case ('min_max_stay') {
              <ng-container *ngTemplateOutlet="minMaxStayTemplate"></ng-container>
            }
            @case ('restricted_check_in_out') {
              <ng-container *ngTemplateOutlet="restrictedCheckInOutTemplate"></ng-container>
            }
            @case ('discounts') {
              <ng-container *ngTemplateOutlet="discountsTemplate"></ng-container>
            }
          }
        </form>
      </ng-container>

      <ng-container ngProjectAs="footer">
        <ng-container *ngTemplateOutlet="footerTemplate"></ng-container>
      </ng-container>
    </sbnb-nested-full-screen-modal>
  }

  <ng-template #formSummaryTemplate>
    <div [formGroup]="rulesetForm">
      <!-- Basic rule set fields -->
      <section class="flex flex-col gap-2">
        <!-- Rule set name -->
        <div>
          <sbnb-form-label label="Rule set name" />

          <mat-form-field class="w-full">
            <input matInput formControlName="name" placeholder="Enter a name for this rule-set" />
            @if (rulesetForm.controls.name.errors?.['required']) {
              <mat-error>{{ t('FORMS.REQUIRED') }}</mat-error>
            } @else if (rulesetForm.controls.name.errors?.['minlength']) {
              <mat-error>{{ t('FORMS.MIN_LENGTH', { minLength: 3 }) }}</mat-error>
            } @else if (rulesetForm.controls.name.errors?.['maxlength']) {
              <mat-error>{{ t('FORMS.MAX_LENGTH', { maxLength: 64 }) }}</mat-error>
            }
          </mat-form-field>
        </div>

        <div>
          <sbnb-form-label label="Color (to be used on your calendar)" />

          <sbnb-color-picker class="block my-2" formControlName="color" />
        </div>
      </section>

      <!-- Pricing section -->
      <section class="mt-6">
        <h3 class="mb-2">Pricing</h3>
        <span class="text-night text-sm leading-6 mb-4"> Set price adjustments for your nightly rates. </span>

        <div class="flex flex-col gap-3 mt-3">
          <sbnb-step-trigger
            [headline]="'Nightly rate'"
            [subline]="rulesetForm.get('seasonal_adjustment')?.value | fn: getPriceAdjustmentSubline"
            (click)="openSection('seasonal_adjustment')"></sbnb-step-trigger>

          <sbnb-step-trigger
            [customHeadline]="true"
            [subline]="rulesetForm.get('discounts')?.value | fn: getDiscountsSubline"
            (click)="openSection('discounts')">
            <div stepTriggerCustomHeadline>
              <div class="flex items-center gap-2">
                <div class="strong text-grape-darker">Discounts</div>
                <div class="flex flex-row gap-1" matTooltip="Discounts only apply to Airbnb and Direct reservations.">
                  <ui-icon icon="airbnb" [size]="16" />
                  <ui-icon icon="direct" [size]="16" />
                </div>
              </div>
            </div>
          </sbnb-step-trigger>
        </div>
      </section>

      <!-- Availability section -->
      <section class="mt-6">
        <h3 class="mb-2">Availability</h3>
        <span class="text-night text-sm leading-6 mb-4">
          Customize minimum stays, prep time, and check-in/check-out options.
        </span>

        <div class="flex flex-col gap-3 mt-3">
          <sbnb-step-trigger
            [headline]="'Trip length'"
            [subline]="rulesetForm.get('min_max_stay')?.value | fn: getMinMaxStaySubline"
            (click)="openSection('min_max_stay')"></sbnb-step-trigger>

          <sbnb-step-trigger
            [headline]="'Restrict check-in/check-out'"
            [subline]="rulesetForm.get('restricted_check_in_out')?.value | fn: getRestrictedCheckInOutSubline"
            (click)="openSection('restricted_check_in_out')"></sbnb-step-trigger>
        </div>
      </section>
    </div>
  </ng-template>

  <ng-template #priceAdjustmentTemplate>
    <div [formGroup]="rulesetForm">
      <div formGroupName="seasonal_adjustment">
        <div class="mb-4">
          <h3 class="mb-2">Nightly rate</h3>
          <span class="text-night leading-6"
            >This will apply on top of the price already set for each property, whether it’s based on manual pricing, a
            third-party tool, or Hospitable’s dynamic pricing.</span
          >
        </div>

        <sbnb-form-label label="Price adjustment" />

        <mat-form-field class="w-full">
          <input matInput formControlName="value" placeholder="No adjustment" type="number" min="0" max="9999" />
          <span matSuffix>%</span>
          @if (rulesetForm.get('seasonal_adjustment.value')?.errors?.['required']) {
            <mat-error>{{ t('FORMS.REQUIRED') }}</mat-error>
          } @else if (rulesetForm.get('seasonal_adjustment.value')?.errors?.['pattern']) {
            <mat-error>{{ t('FORMS.NUMERIC_ONLY') }}</mat-error>
          } @else if (rulesetForm.get('seasonal_adjustment.value')?.errors?.['min']) {
            <mat-error>{{ t('FORMS.MIN_VALUE', { min: 0 }) }}</mat-error>
          } @else if (rulesetForm.get('seasonal_adjustment.value')?.errors?.['max']) {
            <mat-error>{{ t('FORMS.MAX_VALUE', { max: 9999 }) }}</mat-error>
          }
        </mat-form-field>

        <div
          [class.hidden]="
            rulesetForm.get('seasonal_adjustment.value')?.value === 0 ||
            rulesetForm.get('seasonal_adjustment.value')?.value === null
          ">
          <mat-radio-group class="mt-2" formControlName="type">
            <mat-radio-button value="increase" checked>Increase</mat-radio-button>
            <mat-radio-button value="decrease">Decrease</mat-radio-button>
          </mat-radio-group>
        </div>

        <div class="mt-6 mb-4">
          <h3 class="mb-2">Hospitable dynamic pricing</h3>
          <span class="text-night leading-6"
            >Set boundaries for nightly rates for properties that are using Hospitable’s Dynamic Pricing.</span
          >
        </div>

        <sbnb-form-label class="block" label="Min price" />
        <mat-form-field class="w-full">
          <input matInput formControlName="min_price" type="number" placeholder="No min price" min="0" />
        </mat-form-field>

        <sbnb-form-label label="Max price" />
        <mat-form-field class="w-full">
          <input matInput formControlName="max_price" type="number" placeholder="No max price" min="0" />
        </mat-form-field>
      </div>
    </div>
  </ng-template>

  <ng-template #minMaxStayTemplate>
    <div [formGroup]="rulesetForm">
      <div formGroupName="min_max_stay">
        <div class="mb-6">
          <h3 class="mb-2">Min/max stay</h3>
        </div>

        <sbnb-form-label label="Min stay" />
        <mat-form-field class="w-full">
          <input matInput formControlName="min" placeholder="No min stay" type="number" min="0" max="9999" />
          <span matSuffix class="text-night text-sm">nights</span>
          @if (rulesetForm.get('min_max_stay.min')?.errors?.['min']) {
            <mat-error>{{ t('FORMS.MIN_VALUE', { min: 0 }) }}</mat-error>
          } @else if (rulesetForm.get('min_max_stay.min')?.errors?.['max']) {
            <mat-error>{{ t('FORMS.MAX_VALUE', { max: 9999 }) }}</mat-error>
          } @else if (rulesetForm.get('min_max_stay.min')?.errors?.['pattern']) {
            <mat-error>{{ t('FORMS.NUMERIC_ONLY') }}</mat-error>
          }
        </mat-form-field>

        <sbnb-form-label label="Max stay" />
        <mat-form-field class="w-full">
          <input matInput formControlName="max" placeholder="No max stay" type="number" min="0" max="9999" />
          <span matSuffix class="text-night text-sm">nights</span>
          @if (rulesetForm.get('min_max_stay.max')?.errors?.['min']) {
            <mat-error>{{ t('FORMS.MIN_VALUE', { min: 0 }) }}</mat-error>
          } @else if (rulesetForm.get('min_max_stay.max')?.errors?.['max']) {
            <mat-error>{{ t('FORMS.MAX_VALUE', { max: 9999 }) }}</mat-error>
          } @else if (rulesetForm.get('min_max_stay.max')?.errors?.['pattern']) {
            <mat-error>{{ t('FORMS.NUMERIC_ONLY') }}</mat-error>
          }
        </mat-form-field>

        @if (rulesetForm.get('min_max_stay')?.hasError('maxLessThanMin')) {
          <mat-error class="mt-1">Max stay must be greater than min stay.</mat-error>
        }

        <ui-collapsible-card
          [defaultOpen]="rulesetForm.get('min_max_stay')?.value | fn: hasOneCustomizedMinStayByDay"
          class="block mt-4">
          <h4 title>Customize minimum stay by check-in day</h4>

          <ng-template uiCollapsibleCardContent>
            <div formArrayName="min_nights_by_day" class="mt-4 grid grid-cols-2 md:grid-cols-3 gap-4">
              @for (control of minNightsByDayControls; track i; let i = $index) {
                <div>
                  <sbnb-form-label [label]="t(daysOfWeekMapping[daysOfWeekKeys[i]])" />
                  <mat-form-field class="w-full">
                    <input
                      matInput
                      [formControlName]="i"
                      type="number"
                      min="0"
                      max="9999"
                      [placeholder]="rulesetForm.get('min_max_stay.min')?.value" />
                    <span matSuffix class="text-night text-sm">nights</span>
                  </mat-form-field>
                </div>
              }
            </div>
          </ng-template>
        </ui-collapsible-card>
      </div>
    </div>
  </ng-template>

  <ng-template #restrictedCheckInOutTemplate>
    <div [formGroup]="rulesetForm">
      <div formGroupName="restricted_check_in_out">
        <div class="mb-6">
          <h3 class="mb-2">Restricted check-in/check-out</h3>
        </div>

        <sbnb-form-label label="Guests can't check in on" />
        <div formGroupName="check_in" class="mt-3">
          <div class="flex flex-col gap-2">
            @for (day of weekdays; track $index) {
              <div
                (click)="toggleRestrictedCheckInOut('check_in', day)"
                class="flex gap-2 items-center hover:bg-smoke-lighter cursor-pointer">
                <mat-checkbox
                  class="pointer-events-none"
                  [checked]="rulesetForm.get('restricted_check_in_out.check_in')?.value?.includes(day)">
                </mat-checkbox>
                <span>
                  {{ t(daysOfWeekMapping[daysOfWeekKeys[day]]) }}
                </span>
              </div>
            }
          </div>
        </div>

        <div class="mt-6">
          <sbnb-form-label label="Guests can't check out on" />
          <div formGroupName="check_out" class="mt-3">
            <div class="flex flex-col gap-2">
              @for (day of weekdays; track $index) {
                <div
                  (click)="toggleRestrictedCheckInOut('check_out', day)"
                  class="flex gap-2 items-center hover:bg-smoke-lighter cursor-pointer">
                  <mat-checkbox
                    class="pointer-events-none"
                    [checked]="rulesetForm.get('restricted_check_in_out.check_out')?.value?.includes(day)">
                  </mat-checkbox>
                  <span>
                    {{ t(daysOfWeekMapping[daysOfWeekKeys[day]]) }}
                  </span>
                </div>
              }
            </div>
          </div>
        </div>
      </div>
    </div>
  </ng-template>

  <ng-template #discountsTemplate>
    <div [formGroup]="rulesetForm">
      <div formGroupName="discounts">
        <div class="mb-6">
          <h3 class="mb-2">Discounts</h3>
          <span class="text-night leading-6">Applies to Airbnb and Direct reservations only.</span>
        </div>

        <!-- Length of stay discounts -->
        <ng-container>
          <h4>Length of stay discounts</h4>

          <div formArrayName="length_of_stay" class="flex flex-col gap-4 mt-3">
            @for (discountControl of lengthOfStayDiscountControls; track i; let i = $index) {
              <div [formGroupName]="i" class="flex items-center gap-3 p-3 pt-6 border border-gray-200 rounded-md">
                <div class="flex-1">
                  <sbnb-form-label [label]="'For stays of at least'" />
                  <mat-form-field class="w-full">
                    <input matInput formControlName="nights" type="number" min="1" />
                    <span matSuffix class="text-night text-sm">nights</span>
                    @if (discountControl.get('nights')?.errors?.['min']) {
                      <mat-error>{{ t('FORMS.MIN_VALUE', { min: 1 }) }}</mat-error>
                    } @else if (discountControl.get('nights')?.errors?.['pattern']) {
                      <mat-error>{{ t('FORMS.NUMERIC_ONLY') }}</mat-error>
                    }
                  </mat-form-field>
                </div>

                <div class="flex-1">
                  <sbnb-form-label [label]="'Discount'" />
                  <mat-form-field class="w-full">
                    <input matInput formControlName="value" type="number" min="0" max="100" />
                    <span matSuffix class="text-night text-sm">%</span>
                    @if (discountControl.get('value')?.errors?.['min']) {
                      <mat-error>{{ t('FORMS.MIN_VALUE', { min: 0 }) }}</mat-error>
                    } @else if (discountControl.get('value')?.errors?.['max']) {
                      <mat-error>{{ t('FORMS.MAX_VALUE', { max: 100 }) }}</mat-error>
                    } @else if (discountControl.get('value')?.errors?.['pattern']) {
                      <mat-error>{{ t('FORMS.NUMERIC_ONLY') }}</mat-error>
                    }
                  </mat-form-field>
                </div>

                <ui-icon
                  class="cursor-pointer px-2"
                  icon="griddy-trash-alt"
                  htmlButtonType="button"
                  aria-label="Remove discount"
                  (click)="removeLengthOfStayDiscount(i)"></ui-icon>
              </div>
            }
          </div>

          <div class="mt-2">
            <sbnb-button type="secondary" size="mini" htmlButtonType="button" (clicked)="addLengthOfStayDiscount()">
              Add length of stay discount
            </sbnb-button>
          </div>
        </ng-container>

        <!-- Last minute discounts -->
        <ng-container>
          <h4 class="mt-8">Last minute discounts</h4>

          <div formArrayName="last_minute" class="flex flex-col gap-4 mt-3">
            @for (discountControl of lastMinuteDiscountControls; track i; let i = $index) {
              <div [formGroupName]="i" class="flex items-center gap-3 p-3 pt-6 border border-gray-200 rounded-md">
                <div class="flex-1">
                  <sbnb-form-label [label]="'Days before arrival'" />
                  <mat-form-field class="w-full">
                    <input matInput formControlName="days" type="number" min="1" />
                    <span matSuffix class="text-night text-sm">days</span>
                    @if (discountControl.get('days')?.errors?.['min']) {
                      <mat-error>{{ t('FORMS.MIN_VALUE', { min: 1 }) }}</mat-error>
                    } @else if (discountControl.get('days')?.errors?.['pattern']) {
                      <mat-error>{{ t('FORMS.NUMERIC_ONLY') }}</mat-error>
                    }
                  </mat-form-field>
                </div>

                <div class="flex-1">
                  <sbnb-form-label [label]="'Discount'" />
                  <mat-form-field class="w-full">
                    <input matInput formControlName="value" type="number" min="0" max="100" />
                    <span matSuffix class="text-night text-sm">%</span>
                    @if (discountControl.get('value')?.errors?.['min']) {
                      <mat-error>{{ t('FORMS.MIN_VALUE', { min: 0 }) }}</mat-error>
                    } @else if (discountControl.get('value')?.errors?.['max']) {
                      <mat-error>{{ t('FORMS.MAX_VALUE', { max: 100 }) }}</mat-error>
                    } @else if (discountControl.get('value')?.errors?.['pattern']) {
                      <mat-error>{{ t('FORMS.NUMERIC_ONLY') }}</mat-error>
                    }
                  </mat-form-field>
                </div>

                <ui-icon
                  class="cursor-pointer px-2"
                  icon="griddy-trash-alt"
                  htmlButtonType="button"
                  aria-label="Remove discount"
                  (click)="removeLastMinuteDiscount(i)"></ui-icon>
              </div>
            }
          </div>

          <div class="mt-2">
            <sbnb-button type="secondary" size="mini" htmlButtonType="button" (clicked)="addLastMinuteDiscount()">
              Add last minute discount
            </sbnb-button>
          </div>
        </ng-container>

        <!-- Early beard discounts -->
        <ng-container>
          <h4 class="mt-8">Early bird discounts</h4>

          <div formArrayName="early_bird" class="flex flex-col gap-4 mt-3">
            @for (discountControl of earlyBirdDiscountControls; track i; let i = $index) {
              <div [formGroupName]="i" class="flex items-center gap-3 p-3 pt-6 border border-gray-200 rounded-md">
                <div class="flex-1">
                  <sbnb-form-label [label]="'Months before arrival'" />
                  <mat-form-field class="w-full">
                    <input matInput formControlName="months" type="number" min="1" />
                    <span matSuffix class="text-night text-sm">months</span>
                    @if (discountControl.get('months')?.errors?.['min']) {
                      <mat-error>{{ t('FORMS.MIN_VALUE', { min: 1 }) }}</mat-error>
                    } @else if (discountControl.get('months')?.errors?.['pattern']) {
                      <mat-error>{{ t('FORMS.NUMERIC_ONLY') }}</mat-error>
                    }
                  </mat-form-field>
                </div>

                <div class="flex-1">
                  <sbnb-form-label [label]="'Discount'" />
                  <mat-form-field class="w-full">
                    <input matInput formControlName="value" type="number" min="0" max="100" />
                    <span matSuffix class="text-night text-sm">%</span>
                    @if (discountControl.get('value')?.errors?.['min']) {
                      <mat-error>{{ t('FORMS.MIN_VALUE', { min: 0 }) }}</mat-error>
                    } @else if (discountControl.get('value')?.errors?.['max']) {
                      <mat-error>{{ t('FORMS.MAX_VALUE', { max: 100 }) }}</mat-error>
                    } @else if (discountControl.get('value')?.errors?.['pattern']) {
                      <mat-error>{{ t('FORMS.NUMERIC_ONLY') }}</mat-error>
                    }
                  </mat-form-field>
                </div>

                <ui-icon
                  class="cursor-pointer px-2"
                  icon="griddy-trash-alt"
                  htmlButtonType="button"
                  aria-label="Remove discount"
                  (click)="removeEarlyBirdDiscount(i)"></ui-icon>
              </div>
            }
          </div>

          <div class="mt-2">
            <sbnb-button type="secondary" size="mini" htmlButtonType="button" (clicked)="addEarlyBirdDiscount()">
              Add early bird discount
            </sbnb-button>
          </div>
        </ng-container>
      </div>
    </div>
  </ng-template>

  <ng-template #footerTemplate>
    <div class="w-full flex items-center justify-between gap-4">
      <!-- Delete button, overview only -->
      <div>
        @if (activeFormArea() === null && mode() === 'edit') {
          <u
            class="text-error-500 font-bold cursor-pointer"
            [attr.disabled]="isSaving() || isDeleting()"
            (click)="deleteRuleset()"
            >Delete</u
          >
        }
      </div>

      <div class="flex items-center justify-end gap-4">
        <!-- Cancel and save buttons -->
        @if (activeFormArea() === null) {
          <sbnb-button type="secondary" htmlButtonType="button" [disabled]="isSaving()" (clicked)="cancel()">
            Cancel
          </sbnb-button>
        } @else {
          <sbnb-button
            type="secondary"
            htmlButtonType="button"
            [disabled]="isSaving()"
            (clicked)="resetActiveFormAreaAndClearUnsavedChanges(activeFormArea())">
            Cancel
          </sbnb-button>
        }

        @if (activeFormArea() === null) {
          <sbnb-button
            htmlButtonType="submit"
            [disabled]="!rulesetForm.valid"
            [loading]="isSaving()"
            (clicked)="save()">
            {{ modeLabelMapper[mode()] }}
          </sbnb-button>
        } @else {
          <sbnb-button
            htmlButtonType="submit"
            [disabled]="!rulesetForm.get(activeFormArea())?.valid"
            [loading]="isSaving()"
            (clicked)="saveSectionChanges()">
            Save section
          </sbnb-button>
        }
      </div>
    </div>
  </ng-template>

  <ng-template #errorTemplate>
    <div class="flex flex-col gap-4 items-center justify-center h-full">
      <hospitable-page-level-error class="block mt-24" message="Unable to load rule-set"></hospitable-page-level-error>
      <sbnb-button type="secondary" size="mini" htmlButtonType="button" (clicked)="cancel()">Go back</sbnb-button>
    </div>
  </ng-template>

  <ng-template #loadingTemplate>
    <div class="animate-pulse">
      <div class="w-full max-w-[480px] mx-auto mt-6 md:mt-16 px-4 md:px-0">
        <!-- Header Skeleton -->
        <div class="mb-6 w-full">
          <div class="h-8 w-3/4 bg-smoke rounded mb-2"></div>
          <div class="h-6 w-1/2 bg-smoke rounded"></div>
        </div>

        <!-- Form Name Skeleton -->
        <div class="mb-4">
          <div class="h-4 w-1/4 bg-smoke rounded mb-2"></div>
          <div class="h-10 w-full bg-smoke rounded"></div>
        </div>

        <!-- Color Picker Skeleton -->
        <div class="mb-6">
          <div class="h-4 w-1/3 bg-smoke rounded mb-2"></div>
          <div class="h-10 w-full bg-smoke rounded"></div>
        </div>

        <!-- Pricing Section Skeleton -->
        <section class="mt-6">
          <div class="h-6 w-1/4 bg-smoke rounded mb-2"></div>
          <div class="h-4 w-full bg-smoke-lighter rounded mb-4"></div>
          <div class="flex flex-col gap-3 mt-3">
            <div class="h-16 w-full bg-smoke-lighter rounded"></div>
            <div class="h-16 w-full bg-smoke-lighter rounded"></div>
            <div class="h-16 w-full bg-smoke-lighter rounded"></div>
          </div>
        </section>

        <!-- Availability Section Skeleton -->
        <section class="mt-6">
          <div class="h-6 w-1/3 bg-smoke rounded mb-2"></div>
          <div class="h-4 w-full bg-smoke-lighter rounded mb-4"></div>
          <div class="flex flex-col gap-3 mt-3">
            <div class="h-16 w-full bg-smoke-lighter rounded"></div>
            <div class="h-16 w-full bg-smoke-lighter rounded"></div>
          </div>
        </section>
      </div>
    </div>
  </ng-template>
</ng-container>
